<template>
  <div class="sidebar-section" :class="{ 'collapsed': !isExpanded }">
    <!-- Section Header -->
    <div
      class="section-header"
      :class="{ 'clickable': collapsible }"
      @click="collapsible && toggleSection()"
    >
      <div class="header-left">
        <v-icon v-if="icon" small class="section-icon" :color="iconColor">
          {{ icon }}
        </v-icon>
        <span class="section-title">{{ title }}</span>
        <span
          v-if="badge !== null && badge !== undefined"
          class="section-badge"
          :class="badgeColor"
        >
          {{ badge }}
        </span>
      </div>
      <div class="header-right">
        <slot name="header-actions"></slot>
        <v-icon
          v-if="collapsible"
          small
          class="expand-icon"
          :class="{ 'expanded': isExpanded }"
        >
          mdi-chevron-down
        </v-icon>
      </div>
    </div>

    <!-- Section Content -->
    <transition name="section-slide">
      <div v-if="isExpanded" class="section-content">
        <slot></slot>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'SidebarSection',
  props: {
    title: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: null
    },
    iconColor: {
      type: String,
      default: '#909090'
    },
    badge: {
      type: [Number, String],
      default: null
    },
    badgeColor: {
      type: String,
      default: 'default'
    },
    expanded: {
      type: Boolean,
      default: true
    },
    collapsible: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isExpanded: this.expanded
    };
  },
  watch: {
    expanded(newVal) {
      this.isExpanded = newVal;
    }
  },
  methods: {
    toggleSection() {
      this.isExpanded = !this.isExpanded;
      this.$emit('toggle', this.isExpanded);
    },
    expand() {
      this.isExpanded = true;
      this.$emit('toggle', true);
    },
    collapse() {
      this.isExpanded = false;
      this.$emit('toggle', false);
    }
  }
};
</script>

<style scoped>
/* ============================================
   SIDEBAR SECTION
   Professional CAD Panel Style
   ============================================ */

/* Design System Variables - Unified with Measurement Toolbar */
.sidebar-section {
  --bg-primary: #2b2b2b;
  --bg-secondary: #323232;
  --bg-header: #2f2f2f;
  --bg-hover: #383838;

  --border-color: #4a4a4a;
  --border-subtle: #3d3d3d;

  --text-primary: #e0e0e0;
  --text-secondary: #a0a0a0;
  --text-muted: #707070;

  /* Green accent - Unified with toolbar (#03624c family) */
  --accent-primary: #03624c;
  --accent-primary-light: #a8c97a;
  --accent-blue: #4a9eff;
  --accent-green: #03624c;

  --radius: 4px;
  --transition: 150ms ease;
}

.sidebar-section {
  border-bottom: 1px solid var(--border-subtle);
}

.sidebar-section:last-child {
  border-bottom: none;
}

/* ============================================
   SECTION HEADER
   ============================================ */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  min-height: 40px;
  background: var(--bg-header);
  user-select: none;
  transition: background var(--transition);
}

.section-header.clickable {
  cursor: pointer;
}

.section-header.clickable:hover {
  background: var(--bg-hover);
}

/* Header Layout */
.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* Section Icon */
.section-icon {
  flex-shrink: 0;
  font-size: 18px !important;
  opacity: 1;
}

.section-icon.v-icon {
  color: var(--accent-primary-light) !important;
}

/* Section Title */
.section-title {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Badge */
.section-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;
  background: rgba(76, 175, 80, 0.15);
  color: var(--accent-primary-light);
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.section-badge.primary {
  background: rgba(76, 175, 80, 0.2);
  color: var(--accent-primary-light);
  border-color: rgba(76, 175, 80, 0.4);
}

.section-badge.success {
  background: rgba(76, 175, 80, 0.15);
  color: var(--accent-green);
  border-color: rgba(76, 175, 80, 0.3);
}

.section-badge.info {
  background: rgba(38, 198, 218, 0.15);
  color: #26c6da;
  border-color: rgba(38, 198, 218, 0.3);
}

.section-badge.warning {
  background: rgba(255, 152, 0, 0.15);
  color: #ff9800;
  border-color: rgba(255, 152, 0, 0.3);
}

/* Expand Icon */
.expand-icon {
  color: var(--text-muted);
  transition: transform var(--transition), color var(--transition);
  transform: rotate(-90deg);
  font-size: 18px !important;
}

.expand-icon.expanded {
  transform: rotate(0deg);
  color: var(--accent-primary-light);
}

.section-header.clickable:hover .expand-icon {
  color: var(--accent-primary-light);
}

/* ============================================
   SECTION CONTENT
   ============================================ */
.section-content {
  padding: 12px;
  background: var(--bg-primary);
}

/* ============================================
   SLIDE TRANSITION
   ============================================ */
.section-slide-enter-active,
.section-slide-leave-active {
  transition: all 200ms ease;
  overflow: hidden;
}

.section-slide-enter-from,
.section-slide-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.section-slide-enter-to,
.section-slide-leave-from {
  max-height: 1000px;
  opacity: 1;
}
</style>
